import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import {
  getProgramsData,
  getHeroData,
  getForWhomData,
  getLeastProgramContentsData,
  getWhatWillYouLearnData,
  getGraduateProfessionsData,
  getCostData,
  getReviewsData,
  getTeachersData,
  getStudyIsData,
  getExamFreeEnrollmentData,
  getProgramsDataFaculties,
  getBannerData,
  getProgramsQuestions,
  getProgramsVideo,
  getProgramsSeoData,
  Shared,
  getProgramSeoData,
  ProgramsPage,
  ProgramPage,
  LeadForm,
  JournalsPage
} from 'modules'
import { FunctionPropTypes } from 'ui'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'
import {
  ENV,
  PATHS,
  fetcher,
  useAt,
  parseGeneralSlug,
  getAccentColor,
  LOCALES,
  getCurrentJournal
} from 'fractals'
import { JournalPage, getSeoData } from '@/modules/journal-page'
import {
  getAllJournals,
  getFacultiesData,
  getJournalsData,
  getTagsData
} from '@/modules/journals-page/widgets/Journals'
import { getJournalData } from '@/modules/journal-page/widgets/Journal'
import { getFullProgramContentsData } from '@/modules/program-page/widgets/FullProgramContents/fractals/api'
import { getHowTrainingWorksData } from '@/modules/program-page/widgets/HowTrainingWorks/fractals'
import getProgramFAQ from '@/modules/program-page/widgets/FAQ/getProgramFAQ'
import getReviewBlock from '@/modules/program-page/widgets/ReviewBlock/getReviewBlock'
import { Custom404 } from './404'
import { resolveStaticPaths } from '~shared/utils/resove-static-paths'
import { PsychologyProgramPage } from '~pages/program-page/component/psychology-program-page'

type NextJournalPageProps = FunctionPropTypes<typeof JournalsPage> &
  FunctionPropTypes<typeof JournalPage>

type NextGeneralPageProps = FunctionPropTypes<typeof ProgramsPage> &
  FunctionPropTypes<typeof ProgramPage> &
  FunctionPropTypes<typeof LeadForm> &
  FunctionPropTypes<typeof Custom404> &
  NextJournalPageProps

const NextGeneralPage: NextPage<NextGeneralPageProps> = ({
  bread,
  journal,
  isJournal,
  tags,
  journals,
  allJournals,
  categories,
  images,
  faculties,
  banner,
  programs,
  program,
  footer,
  hero,
  diplomas,
  whyUs,
  videos,
  whyUsLegacy,
  forWhom,
  accentColor,
  whatWillYouLearn,
  graduateProfessions,
  cost,
  leastProgramContents,
  fullProgramContents,
  questions,
  studySteps,
  reviews,
  teachers,
  studyIs,
  examFreeEnrollment,
  seo,
  seoJournal,
  seoJournals,
  howTrainingWorksCards
}: NextGeneralPageProps) => {
  const { isProgramsPage, isJournalsPage, isProgramPage } = useAt()

  if (isProgramsPage)
    return (
      <ProgramsPage
        categories={categories && categories}
        images={images && images}
        programs={programs && programs}
        faculties={faculties && faculties}
        footer={footer && footer}
        seo={seo && seo}
        questions={questions}
        videos={videos}
        banner={banner}
      />
    )

  if (isJournalsPage) {
    return (
      <JournalsPage
        seoJournals={seoJournals}
        tags={tags}
        faculties={faculties}
        journals={journals}
        allJournals={allJournals}
        footer={footer}
      />
    )
  }

  if (isJournal) {
    return (
      <JournalPage
        seoJournal={seoJournal}
        bread={bread}
        journal={journal}
        footer={footer}
      />
    )
  }

  if (isProgramPage) {
    return (
      <ProgramPage
        hero={hero}
        diplomas={diplomas}
        whyUs={whyUs}
        whyUsLegacy={whyUsLegacy}
        forWhom={forWhom}
        whatWillYouLearn={whatWillYouLearn}
        accentColor={accentColor}
        graduateProfessions={graduateProfessions}
        leastProgramContents={leastProgramContents}
        fullProgramContents={fullProgramContents}
        program={program}
        questions={questions}
        cost={cost}
        studySteps={studySteps}
        reviews={reviews}
        teachers={teachers}
        studyIs={studyIs}
        examFreeEnrollment={examFreeEnrollment}
        footer={footer}
        seo={seo}
        howTrainingWorksCards={howTrainingWorksCards}
      />
    )
  }
  return <Custom404 categories={categories} footer={footer} />
}

export const getStaticProps: GetStaticProps = async context => {
  try {
    const { isProgramsPage, isJournalsPage, isProgramPage, redirect } =
      parseGeneralSlug(context?.params?.general?.toString() || null)

    if (redirect?.destination && context.locale === 'ru') {
      return {
        redirect: {
          destination: redirect.destination,
          permanent: true
        }
      }
    }

    const isJournal = await getCurrentJournal({ context })

    if (isProgramsPage) {
      const categories = await Shared.getHeaderData({ context })
      const images = await Shared.getProgramImagesData({ context })
      const programs = await getProgramsData({ context })
      const faculties = await getProgramsDataFaculties({ context })
      const seo = await getProgramsSeoData({ context })
      const footer = await Shared.getFooterData({ context })
      const videos = await getProgramsVideo({ context })
      const banner = await getBannerData()
      const questions = await getProgramsQuestions({ context })

      return {
        props: {
          categories: categories || null,
          images: images || null,
          faculties: faculties || null,
          programs: programs || null,
          footer: footer || null,
          seo: seo || null,
          videos: videos || null,
          banner: banner || null,
          questions: questions || null
        }
      }
    }

    if (isJournalsPage) {
      const seoJournals = await getSeoData({ context })
      const tags = await getTagsData({ context })
      const faculties = await getFacultiesData({ context })
      const journals = await getJournalsData({ context })
      const allJournals = await getAllJournals({ context })
      const footer = await Shared.getFooterData({ context })

      return {
        props: {
          seoJournals: seoJournals || null,
          tags: tags || null,
          faculties: faculties || null,
          journals: journals || null,
          allJournals: allJournals || null,
          footer: footer || null
        }
      }
    }

    if (isJournal) {
      const isJournalBool = await getCurrentJournal({ context })
      const tags = await getTagsData({ context })
      const bread = await Shared.getBreadcrumbs({ context })
      const journal = await getJournalData({ context })
      const seoJournal = await getSeoData({ context })
      const footer = await Shared.getFooterData({ context })

      return {
        props: {
          isJournal: isJournalBool || null,
          tags: tags || null,
          bread: bread || null,
          journal: journal || null,
          seoJournal: seoJournal || null,
          footer: footer || null
        }
      }
    }

    if (isProgramPage) {
      const hero = await getHeroData({ context })
      const diplomas = await Shared.getDiplomasData({
        context
      })
      const whyUs = await Shared.getWhyUsData({ context })
      const whyUsLegacy = await Shared.getWhyUsDataLegacy({ context })
      const forWhom = await getForWhomData({ context })
      const leastProgramContents = await getLeastProgramContentsData({
        context
      })
      const fullProgramContents = await getFullProgramContentsData({
        context
      })
      const program = await Shared.getLeastProgramContentData({ context })
      const whatWillYouLearn = await getWhatWillYouLearnData({ context })
      const graduateProfessions = await getGraduateProfessionsData({ context })
      const cost = await getCostData({ context })
      const accentColor = await getAccentColor({ context })
      const questions = await getProgramFAQ({ context })
      const studySteps = await Shared.getStudyStepsData({ context })
      const reviews = await getReviewBlock({ context })
      const teachers = await getTeachersData({ context })
      const studyIs = await getStudyIsData({ context })
      const examFreeEnrollment = await getExamFreeEnrollmentData({ context })
      const footer = await Shared.getFooterData({ context })
      const seo = await getProgramSeoData({ context })
      const howTrainingWorksCards = await getHowTrainingWorksData({ context })

      return {
        props: {
          hero: hero || null,
          diplomas: diplomas || null,
          whyUs: whyUs || null,
          whyUsLegacy: whyUsLegacy || null,
          forWhom: forWhom || null,
          accentColor: accentColor || null,
          whatWillYouLearn: whatWillYouLearn || null,
          graduateProfessions: graduateProfessions || null,
          cost: cost || null,
          leastProgramContents: leastProgramContents || null,
          fullProgramContents: fullProgramContents || null,
          program: program || null,
          questions: questions || null,
          studySteps: studySteps || null,
          reviews: reviews || null,
          teachers: teachers || null,
          studyIs: studyIs || null,
          examFreeEnrollment: examFreeEnrollment || null,
          footer: footer || null,
          seo: seo || null,
          howTrainingWorksCards
        }
      }
    }

    if (!(isJournal && isJournalsPage && isProgramPage && isProgramsPage)) {
      return {
        notFound: true
      }
    }

    const categories = await Shared.getHeaderData({ context })
    const footer = await Shared.getFooterData({ context })

    const props = {
      isJournal: isJournal || null,
      categories: categories || null,
      footer: footer || null
    }

    const revalidate = ENV.prod ? 60 * 60 * 24 : false // 1 day

    return {
      props,
      revalidate
    }
  } catch (err) {
    console.error(err)
  }

  return {
    props: {}
  }
}

// TODO: Refactor this & generalize it, structure it
export const getStaticPaths: GetStaticPaths<
  ParsedUrlQuery & {
    general: string
  }
> = async ({ locales, defaultLocale }) => {
  try {
    // build query and make request
    const allResponsesProgramsPageCategories =
      locales &&
      (await Promise.all(
        locales.map(async locale => {
          const query = qs.stringify(
            {
              fields: ['slug', 'notUniqueSlug', 'locale'],
              locale
            },
            {
              encodeValuesOnly: true // prettify URL
            }
          )

          const res = await fetcher<{
            data: {
              slug: string | null
              notUniqueSlug: string | null
              locale: string
            }[]
          }>(
            `${PATHS.back.root.index}${PATHS.back.api.categories.index}?${query}`
          )

          return res
        })
      ))

    // normalize data
    const pathsProgramsPageCategories =
      allResponsesProgramsPageCategories?.reduce<
        Array<{
          params: {
            general: string
          }
          locale: string
        }>
      >(
        (acc, cur) => {
          return [
            ...acc,
            ...(cur?.data?.map((item, idx) => {
              const { locale } = item

              const params = {
                general: item.notUniqueSlug || item.slug || 'programmy'
              }

              return {
                params,
                locale
              }
            }) || [])
          ]
        },
        [
          {
            params: {
              general: 'programmy'
            },
            locale: LOCALES.ru
          },
          {
            params: {
              general: 'programs'
            },
            locale: LOCALES.en
          },
          {
            params: {
              general: 'programlar'
            },
            locale: LOCALES.uz
          },
          {
            params: {
              general: 'programlar'
            },
            locale: LOCALES.kk
          }
        ]
      ) || []

    // build query and make request
    const allResponsesProgramsPageFaculties =
      locales &&
      (await Promise.all(
        locales.map(async locale => {
          const query = qs.stringify(
            {
              fields: ['slug', 'notUniqueSlug', 'locale'],
              populate: {
                category: {
                  fields: ['slug', 'notUniqueSlug', 'locale']
                },
                faculty: {
                  fields: ['slug', 'locale']
                }
              },
              locale
            },
            {
              encodeValuesOnly: true // prettify URL
            }
          )

          const facultiesQuery = qs.stringify(
            {
              fields: ['slug', 'locale'],
              locale
            },
            {
              encodeValuesOnly: true // prettify URL
            }
          )

          const res = await fetcher<{
            data: {
              slug: string | null
              notUniqueSlug?: string | null
              locale: string
              category?: {
                slug: string | null
                notUniqueSlug: string | null
                locale: string
              }
              faculty?: {
                slug: string | null
                locale: string
              }
            }[]
          }>(
            `${PATHS.back.root.index}${PATHS.back.api.programs.index}?${query}`
          )

          const facultiesRes = await fetcher<{
            data: {
              slug: string | null
              locale: string
            }[]
          }>(
            `${PATHS.back.root.index}${PATHS.back.api.faculties.index}?${facultiesQuery}`
          )

          if (res?.data && facultiesRes?.data) {
            facultiesRes.data = facultiesRes.data.map(faculty => ({
              ...faculty,
              faculty: {
                slug: faculty.slug
              }
            }))

            res.data = [...res.data, ...facultiesRes.data]
          }

          return res
        })
      ))

    // normalize data
    const pathsProgramsPageFaculties =
      allResponsesProgramsPageFaculties?.reduce<
        Array<{
          params: {
            general: string
          }
          locale: string
        }>
      >((acc, cur) => {
        return [
          ...acc,
          ...(cur?.data?.map((item, idx) => {
            const { locale, category, faculty } = item

            const categorySlug =
              category?.notUniqueSlug || category?.slug || null

            const facultySlug = faculty?.slug || null

            const facultyKeys = {
              ru: 'fakultet',
              en: 'faculty',
              uz: 'fakultet',
              kk: 'fakultetler'
            } as const

            const programsKeys = {
              ru: 'programmy',
              en: 'programs',
              uz: 'programlar',
              kk: 'programlar'
            }

            const params = {
              general: `${
                categorySlug || programsKeys[locale as keyof typeof LOCALES]
              }${
                (facultySlug &&
                  `--${
                    facultyKeys[(locale as keyof typeof LOCALES) || 'ru']
                  }--${facultySlug}`) ||
                ''
              }`
            }

            return {
              params,
              locale
            }
          }) || [])
        ]
      }, []) || []

    // build query and make request
    const allResponsesProgramPage =
      locales &&
      (await Promise.all(
        locales.map(async locale => {
          const query = qs.stringify(
            {
              fields: ['slug', 'notUniqueSlug', 'locale'],
              populate: {
                category: {
                  fields: [
                    'slug',
                    'notUniqueSlug',
                    'slugSingular',
                    'notUniqueSlugSingular'
                  ]
                }
              },
              locale
            },
            {
              encodeValuesOnly: true // prettify URL
            }
          )

          const res = await fetcher<{
            data: {
              slug: string
              notUniqueSlug: string | null
              locale: string
              category: {
                slug: string | null
                notUniqueSlug: string | null
                slugSingular: string | null
                notUniqueSlugSingular: string | null
              }
            }[]
          }>(
            `${PATHS.back.root.index}${PATHS.back.api.programs.index}?${query}`
          )

          return res
        })
      ))

    // normalize data
    const pathsProgramPage =
      allResponsesProgramPage?.reduce<
        Array<{
          params: {
            general: string
          }
          locale: string
        }>
      >((acc, cur) => {
        return [
          ...acc,
          ...(cur?.data?.map((item, idx) => {
            const categorySlug =
              item.category?.notUniqueSlugSingular ||
              item.category?.slugSingular ||
              item.category?.notUniqueSlug ||
              item.category?.slug ||
              'programmy'

            const { locale, slug, notUniqueSlug } = item

            const programSlug = notUniqueSlug || slug

            const general = `${programSlug}--${categorySlug}`

            const params = {
              general
            }

            return {
              params,
              locale
            }
          }) || [])
        ]
      }, []) || []

    // build query and make request
    const allResponsesJournalsPageFilter =
      locales &&
      (await Promise.all(
        locales.map(async locale => {
          const query = qs.stringify(
            {
              fields: ['slug', 'locale'],
              populate: {
                faculty: {
                  fields: ['slug', 'locale']
                },
                tag: {
                  fields: ['slug', 'locale']
                }
              },
              locale
            },
            {
              encodeValuesOnly: true // prettify URL
            }
          )

          const res = await fetcher<{
            data: {
              slug: string | null
              notUniqueSlug: string | null
              locale: string
              faculty: {
                slug: string | null
                locale: string
              }
              tag: {
                slug: string | null
                locale: string
              }
            }[]
          }>(
            `${PATHS.back.root.index}${PATHS.back.api.journals.index}?${query}`
          )

          return res
        })
      ))

    // normalize data
    const pathsJournalsPageFilter =
      allResponsesJournalsPageFilter?.reduce<
        Array<{
          params: {
            general: string
          }
          locale: string
        }>
      >(
        (acc, cur) => {
          return [
            ...acc,
            ...(cur?.data?.map((item, idx) => {
              const { faculty, tag, locale } = item || 'ru'
              const facultySlug = faculty?.slug || ''
              const tagSlug = tag?.slug || ''

              const params = {
                general: `journal--${facultySlug || tagSlug}` || 'journal'
              }

              return {
                params,
                locale
              }
            }) || [])
          ]
        },
        [
          {
            params: {
              general: 'journal'
            },
            locale: LOCALES.ru
          },
          {
            params: {
              general: 'journal'
            },
            locale: LOCALES.en
          },
          {
            params: {
              general: 'jurnal'
            },
            locale: LOCALES.uz
          },
          {
            params: {
              general: 'jwrnal'
            },
            locale: LOCALES.kk
          }
        ]
      ) || []

    // build query and make request
    const allResponsesJournalPage =
      locales &&
      (await Promise.all(
        locales.map(async locale => {
          const query = qs.stringify(
            {
              fields: ['slug', 'locale'],
              populate: {
                faculty: {
                  fields: ['slug', 'locale']
                },
                tag: {
                  fields: ['slug', 'locale']
                }
              },

              locale
            },
            {
              encodeValuesOnly: true // prettify URL
            }
          )

          const res = await fetcher<{
            data: {
              slug: string
              locale: string
              faculty: {
                slug: string | null
                locale: string
              }
              tag: {
                slug: string | null
                locale: string
              }
            }[]
          }>(
            `${PATHS.back.root.index}${PATHS.back.api.journals.index}?${query}`
          )

          return res
        })
      ))

    // normalize data
    const pathsJournalPage =
      allResponsesJournalPage?.reduce<
        Array<{
          params: {
            general: string
          }
          locale: string
        }>
      >((acc, cur) => {
        return [
          ...acc,
          ...(cur?.data?.map((item, idx) => {
            const { locale, faculty, tag } = item || 'ru'

            const articleSlug = item?.slug || ''
            const facultySlug = faculty?.slug || ''
            const tagSlug = tag?.slug || ''

            const general =
              `journal--${facultySlug || tagSlug}--${articleSlug}` || ''
            // `articles--${categorySlug}--${facultySlug}--${articleSlug}`

            const params = {
              general
            }

            return {
              params,
              locale
            }
          }) || [])
        ]
      }, []) || []

    const paths = [
      ...pathsProgramsPageCategories,
      ...pathsProgramsPageFaculties,
      ...pathsProgramPage,
      ...pathsJournalsPageFilter,
      ...pathsJournalPage
    ]

    // return paths

    return {
      paths,
      fallback: 'blocking'
    }
  } catch (err) {
    console.error(err)
    return {
      paths: [],
      fallback: 'blocking'
    }
  }
}

export default NextGeneralPage
